import { useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ListPagination } from "@/components/shared/ListPagination";
import { DataTable, type DataTableColumn } from "@/components/shared/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useInvoicesList,
  useMarkInvoicePaid,
  useCancelInvoice,
  type InvoiceRow,
  type InvoiceType,
  type InvoiceStatus,
} from "@/hooks/useInvoices";
import { formatDate, formatCurrency } from "@/utils/format";
import { downloadCsv } from "@/utils/exportCsv";
import { FileText, CheckCircle2, XCircle } from "lucide-react";

const DEFAULT_PAGE_SIZE = 25;

export const InvoicesList = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [typeFilter, setTypeFilter] = useState<InvoiceType | "ALL">("ALL");
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | "ALL">(
    "ALL",
  );
  const [search, setSearch] = useState("");

  const [payTarget, setPayTarget] = useState<InvoiceRow | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("UPI");
  const [paymentRef, setPaymentRef] = useState("");

  const [detailTarget, setDetailTarget] = useState<InvoiceRow | null>(null);

  const { data, isLoading, error, refetch } = useInvoicesList({
    page,
    limit,
    type: typeFilter === "ALL" ? undefined : typeFilter,
    status: statusFilter === "ALL" ? undefined : statusFilter,
    search: search || undefined,
  });

  const markPaid = useMarkInvoicePaid();
  const cancelInvoice = useCancelInvoice();

  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const handleMarkPaid = () => {
    if (!payTarget) return;
    markPaid.mutate(
      {
        id: payTarget.id,
        paymentMethod,
        paymentReference: paymentRef,
      },
      {
        onSuccess: () => {
          toast.success(`Invoice ${payTarget.invoiceNumber} marked as PAID`);
          setPayTarget(null);
          setPaymentRef("");
          refetch();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const handleCancel = (id: string, invoiceNumber: string) => {
    if (!confirm(`Are you sure you want to cancel invoice ${invoiceNumber}?`)) {
      return;
    }
    cancelInvoice.mutate(
      { id, reason: "Cancelled by admin" },
      {
        onSuccess: () => {
          toast.success(`Invoice ${invoiceNumber} cancelled`);
          refetch();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const columns: Array<DataTableColumn<InvoiceRow>> = [
    {
      key: "invoiceNumber",
      header: "Invoice #",
      render: (inv: any) => (
        <button
          type="button"
          onClick={() => setDetailTarget(inv)}
          className="font-mono text-sm font-semibold text-primary hover:underline text-left"
        >
          {inv.invoiceNumber}
        </button>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (inv: any) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            inv.type === "BUSINESS_REGISTRATION"
              ? "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300"
              : "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300"
          }`}
        >
          {inv.type === "BUSINESS_REGISTRATION"
            ? "Registration Fee"
            : "Subscription"}
        </span>
      ),
    },
    {
      key: "owner",
      header: "Business / Owner",
      render: (inv: any) => (
        <div>
          <div className="font-medium text-sm">
            {inv.business?.businessName ?? inv.owner?.name ?? "—"}
          </div>
          {inv.business?.businessName && inv.owner?.name && (
            <div className="text-xs text-muted-foreground">
              Owner: {inv.owner.name} ({inv.owner.phone})
            </div>
          )}
        </div>
      ),
    },
    {
      key: "amount",
      header: "Total Amount",
      align: "right",
      render: (inv: any) => (
        <span className="font-semibold text-sm">
          {formatCurrency(inv.totalAmount)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (inv: any) => <StatusBadge status={inv.status} />,
    },
    {
      key: "issueDate",
      header: "Issue Date",
      render: (inv: any) => (
        <span className="text-xs text-muted-foreground">
          {formatDate(inv.issueDate)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (inv: any) => (
        <div className="flex justify-end gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDetailTarget(inv)}
          >
            <FileText className="w-3.5 h-3.5 mr-1" /> View
          </Button>
          {inv.status === "PENDING" && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                onClick={() => setPayTarget(inv)}
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Paid
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => handleCancel(inv.id, inv.invoiceNumber)}
              >
                <XCircle className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <AppLayout title="Invoices Management">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold">Invoices</h2>
          <p className="text-sm text-muted-foreground">
            Manage business registration fees, subscription invoices, and
            payment statuses.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            downloadCsv(
              "invoices",
              [
                "Invoice #",
                "Type",
                "Owner",
                "Business",
                "Amount",
                "Status",
                "Issue Date",
              ],
              rows.map((inv) => [
                inv.invoiceNumber,
                inv.type,
                inv.owner?.name ?? "",
                inv.business?.businessName ?? "",
                String(inv.totalAmount),
                inv.status,
                formatDate(inv.issueDate),
              ]),
            )
          }
        >
          Export CSV
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search by invoice #, owner, business..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 text-sm"
          />
        </div>

        <div className="flex gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
            className="h-9 px-3 py-1 rounded-btn text-xs border border-border bg-card"
          >
            <option value="ALL">All Types</option>
            <option value="BUSINESS_REGISTRATION">Registration Fee</option>
            <option value="SUBSCRIPTION">Subscription</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="h-9 px-3 py-1 rounded-btn text-xs border border-border bg-card"
          >
            <option value="ALL">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PAID">Paid</option>
            <option value="FAILED">Failed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
      </div>

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={refetch}
        emptyTitle="No invoices found"
        emptyDescription="Invoices for registration fees and subscriptions will appear here."
      >
        <DataTable
          columns={columns as any}
          rows={rows}
          rowKey={(inv: any) => inv.id}
        />
        {pagination && (
          <ListPagination
            pagination={pagination}
            page={page}
            rowsShown={rows.length}
            itemLabel="invoices"
            onPageChange={setPage}
            limit={limit}
            onLimitChange={(n) => {
              setLimit(n);
              setPage(1);
            }}
          />
        )}
      </ListShell>

      {/* Mark Paid Dialog */}
      <Dialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-muted-foreground">
              Invoice{" "}
              <span className="font-semibold text-foreground">
                {payTarget?.invoiceNumber}
              </span>{" "}
              for{" "}
              <span className="font-medium text-foreground">
                {formatCurrency(payTarget?.totalAmount ?? 0)}
              </span>
              .
            </p>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Payment Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full h-9 px-3 text-sm rounded-btn border border-border bg-card"
              >
                <option value="UPI">UPI / GPay / PhonePe</option>
                <option value="BANK_TRANSFER">
                  Bank Transfer / NEFT / IMPS
                </option>
                <option value="CASH">Cash Payment</option>
                <option value="CHEQUE">Cheque</option>
                <option value="CARD">Credit / Debit Card</option>
                <option value="RAZORPAY">Razorpay</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Payment Reference / Transaction ID
              </label>
              <Input
                placeholder="e.g. UTR / Transaction reference number"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleMarkPaid}
              disabled={markPaid.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {markPaid.isPending ? "Updating..." : "Mark as Paid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!detailTarget} onOpenChange={() => setDetailTarget(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center pr-6">
              <span>Invoice {detailTarget?.invoiceNumber}</span>
              {detailTarget && <StatusBadge status={detailTarget.status} />}
            </DialogTitle>
          </DialogHeader>
          {detailTarget && (
            <div className="space-y-4 py-2 text-sm">
              <div className="grid grid-cols-2 gap-3 p-3 rounded-card bg-muted/30 border border-border">
                <div>
                  <dt className="text-xs text-muted-foreground">Type</dt>
                  <dd className="font-medium mt-0.5">
                    {detailTarget.type === "BUSINESS_REGISTRATION"
                      ? "Business Registration Fee"
                      : "Subscription Payment"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Total Amount
                  </dt>
                  <dd className="font-semibold text-base text-primary mt-0.5">
                    {formatCurrency(detailTarget.totalAmount)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Owner</dt>
                  <dd className="mt-0.5">{detailTarget.owner?.name ?? "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Phone / Email
                  </dt>
                  <dd className="mt-0.5">
                    {detailTarget.owner?.phone}{" "}
                    {detailTarget.owner?.email
                      ? `· ${detailTarget.owner.email}`
                      : ""}
                  </dd>
                </div>
                {detailTarget.business && (
                  <div className="col-span-2">
                    <dt className="text-xs text-muted-foreground">
                      Business Centre
                    </dt>
                    <dd className="font-medium mt-0.5">
                      {detailTarget.business.businessName}
                    </dd>
                  </div>
                )}
              </div>

              <div className="space-y-1 text-xs text-muted-foreground">
                <div>Description: {detailTarget.description ?? "N/A"}</div>
                <div>Issued On: {formatDate(detailTarget.issueDate)}</div>
                {detailTarget.dueDate && (
                  <div>Due Date: {formatDate(detailTarget.dueDate)}</div>
                )}
                {detailTarget.paidAt && (
                  <div>Paid At: {formatDate(detailTarget.paidAt)}</div>
                )}
                {detailTarget.paymentMethod && (
                  <div>Payment Method: {detailTarget.paymentMethod}</div>
                )}
                {detailTarget.paymentReference && (
                  <div>Reference: {detailTarget.paymentReference}</div>
                )}
                {detailTarget.periodStart && detailTarget.periodEnd && (
                  <div>
                    Subscription Period: {formatDate(detailTarget.periodStart)}{" "}
                    – {formatDate(detailTarget.periodEnd)}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDetailTarget(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};
